import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import axios from "axios";

import { API_ROUTES } from "../../api/ApiRoutes";
import {
  applyManualTaskPassToProgressResponse,
  mergeManualPassesIntoProgressResponse,
} from "./userProgressMappers";
import type {
  GamePassPassUserTaskRequest,
  GamePassPassUserTaskResponse,
  GamePassUserProgressResponse,
  ManualTaskPassUpdateVariables,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";

export type PassUserTaskVariables = ManualTaskPassUpdateVariables & {
  userId: string;
};

const buildPassUserTaskUrl = (userId: string, taskId: string): string =>
  API_ROUTES.PASS_GAME_PASS_USER_TASK.replace("{user_id}", userId).replace(
    "{task_id}",
    taskId,
  );

const assertPassUserTaskSuccess = (
  data: GamePassPassUserTaskResponse | undefined,
): GamePassPassUserTaskResponse => {
  if (data && typeof data === "object" && data.success === false) {
    const message =
      typeof data.message === "string" && data.message.trim()
        ? data.message.trim()
        : "Failed to pass task";

    throw Object.assign(new Error(message), {
      response: { data },
    });
  }

  return data ?? {};
};

const postPassUserTask = async ({
  userId,
  taskId,
  reason,
  betId,
}: PassUserTaskVariables): Promise<GamePassPassUserTaskResponse> => {
  const body: GamePassPassUserTaskRequest = {
    reason,
    ...(betId ? { bet_id: betId } : {}),
  };
  const response = await getPrivateInstance().post<GamePassPassUserTaskResponse>(
    buildPassUserTaskUrl(userId, taskId),
    body,
  );
  return assertPassUserTaskSuccess(response.data);
};

/**
 * Progress cache key matching `useGetUserProgress` (season) /
 * `useGetGamePassUserCampaignProgress` (campaign). The inputs are mutually
 * exclusive per pass; if both were ever set, season wins — consistent with
 * the season-keyed optimistic patch below.
 */
const getProgressQueryKey = (
  userId: string,
  variables: Pick<PassUserTaskVariables, "seasonId" | "campaignId">,
): readonly [
  "game-pass",
  "users",
  "progress",
  string,
  "season" | "campaign",
  string,
] => {
  const containerId = variables.seasonId ?? variables.campaignId ?? "";
  return [
    "game-pass",
    "users",
    "progress",
    userId,
    variables.seasonId ? "season" : "campaign",
    containerId,
  ] as const;
};

const isTaskAlreadyCompletedError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 409;

const refreshUserProgressQueries = async (
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
) => {
  await queryClient.refetchQueries({
    queryKey: ["game-pass", "users", "progress", userId],
  });
  await queryClient.refetchQueries({
    queryKey: ["game-pass", "users", "stats", userId],
  });
};

export const usePassUserTask = (): UseMutationResult<
  GamePassPassUserTaskResponse,
  Error,
  PassUserTaskVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "users", "tasks", "pass"],
    mutationFn: postPassUserTask,
    onSuccess: async (_data, variables) => {
      const { userId } = variables;
      const progressQueryKey = getProgressQueryKey(userId, variables);

      // Optimistic cache patch is season-keyed today (`season_id#challenge_id`).
      // Campaign passes skip the local patch and rely on the refetch below.
      const canOptimisticPatch = Boolean(variables.seasonId);
      const cachedProgress = canOptimisticPatch
        ? queryClient.getQueryData<GamePassUserProgressResponse>(
            progressQueryKey,
          )
        : undefined;
      const optimisticProgress = canOptimisticPatch
        ? applyManualTaskPassToProgressResponse(cachedProgress, {
            ...variables,
            seasonId: variables.seasonId!,
          }) ?? cachedProgress
        : undefined;

      if (optimisticProgress) {
        queryClient.setQueryData(progressQueryKey, optimisticProgress);
      }
      await refreshUserProgressQueries(queryClient, userId);
      if (optimisticProgress) {
        queryClient.setQueryData<GamePassUserProgressResponse>(
          progressQueryKey,
          (refetched) =>
            mergeManualPassesIntoProgressResponse(
              refetched,
              optimisticProgress,
            ) ?? refetched,
        );
      }
    },
    onError: async (error, variables) => {
      if (!isTaskAlreadyCompletedError(error)) {
        return;
      }

      const { userId } = variables;
      const progressQueryKey = getProgressQueryKey(userId, variables);

      await refreshUserProgressQueries(queryClient, userId);
      if (variables.seasonId) {
        queryClient.setQueryData<GamePassUserProgressResponse>(
          progressQueryKey,
          (refetched) =>
            applyManualTaskPassToProgressResponse(refetched, {
              ...variables,
              seasonId: variables.seasonId!,
            }) ?? refetched,
        );
      }
    },
  });
};

export {
  assertPassUserTaskSuccess,
  buildPassUserTaskUrl,
  postPassUserTask,
  isTaskAlreadyCompletedError,
};
